/**
 * Pages through a limit/offset list endpoint until it returns a short page.
 *
 * Use this for lookups and pickers that must see the WHOLE list: a single
 * capped request silently drops every row past the cap, which renders as
 * missing data ("Unknown Contact", a blank CSV cell, an absent employee).
 *
 * `pageSize` must not exceed the server's per-request maximum
 * (`ParsePaginationParams` accepts up to 1000). A server that clamps below
 * `pageSize` returns a short first page, and the loop stops early.
 *
 * `maxPages` bounds the loop if the server ignores `offset` and keeps
 * returning full pages. Reaching it throws instead of returning a truncated
 * list, because a truncated list is the bug this helper exists to prevent.
 */
export async function fetchAllPages<T>(
  fetchPage: (limit: number, offset: number) => Promise<T[]>,
  pageSize = 100,
  maxPages = 50
): Promise<T[]> {
  const all: T[] = [];
  for (let page = 0; page < maxPages; page += 1) {
    const rows = (await fetchPage(pageSize, page * pageSize)) ?? [];
    all.push(...rows);
    if (rows.length < pageSize) {
      return all;
    }
  }
  throw new Error(
    `fetchAllPages: more than ${maxPages} pages of ${pageSize} rows; refusing to return a truncated list`
  );
}
